import { useEffect, useRef } from "react";
import {
  Alert,
  AsyncSection,
  asyncStatus,
  Button,
  SegmentedControl,
  Select,
  SettingRow,
  SettingsGroup,
  Skeleton,
  Switch,
} from "@playbookai/ui";
import { usePersonalPreferences } from "../../player-ai/usePersonalPreferences.js";
import { PersonalProfileControl } from "../../player-ai/PersonalProfileControl.js";
import {
  PLAYER_AI_LANGUAGE,
  PLAYER_AI_LANGUAGE_OPTIONS,
} from "../../player-ai/player-ai-language.js";
import { settingsApi } from "../../api/settings-client.js";
import { SHOW_MOOD_ACCESS_SETTING } from "./player-ai-settings-visibility.js";

export function PlayerAIGeneralSettings() {
  const state = usePersonalPreferences();
  const imported = useRef(false);
  useEffect(() => {
    if (!state.query.data || state.query.data.tone || imported.current) return;
    imported.current = true;
    void settingsApi
      .getSettings()
      .then((legacy) =>
        state.save({
          tone: ["Coaching", "Encouraging"].includes(legacy.playerai.tone)
            ? "empathetic"
            : "direct",
        }),
      )
      .catch(() => {
        // Optional legacy import: keep current preferences and retry on a later refresh.
        imported.current = false;
      });
  }, [state.query.data]);
  if (!state.query.data)
    return (
      <AsyncSection
        status={asyncStatus({
          isLoading: state.query.isPending,
          isError: state.query.isError,
          isEmpty: false,
          hasData: false,
        })}
        loadingLabel="your Player AI settings"
        errorTitle="Couldn't load your Player AI settings"
        errorDescription={state.query.error?.message}
        onRetry={() => void state.query.refetch()}
        skeleton={<Skeleton className="h-40" />}
        children={null}
      />
    );
  const settings = { ...state.query.data, ...state.failed };
  /* The Direction B rows in a settings group whose name is the selected
     "General" tab right above it, so the heading is for assistive
     technology only. */
  return (
    <SettingsGroup variant="settings" title="General" titleHidden>
      <SettingRow
        label={<span id="personal-tone">Tone</span>}
        description="How Player AI communicates."
        control={
          <SegmentedControl
            aria-labelledby="personal-tone"
            value={settings.tone ?? "direct"}
            options={[
              { value: "direct", label: "Direct", disabled: state.pending },
              {
                value: "empathetic",
                label: "Empathetic",
                disabled: state.pending,
              },
            ]}
            onChange={(tone) =>
              void state.save({ tone: tone as "direct" | "empathetic" })
            }
          />
        }
      />
      {/* Task 2 — English is the only language for now, fixed by the core
          prompt whatever a message or document is written in; this row says
          so. Further languages are future work. */}
      <SettingRow
        label="Player AI language"
        description="The language Player AI responds in."
        control={
          <div className="w-full sm:w-72">
            <Select
              id="player-ai-language"
              ariaLabel="Player AI language"
              value={PLAYER_AI_LANGUAGE}
              options={PLAYER_AI_LANGUAGE_OPTIONS}
            />
          </div>
        }
      />
      {SHOW_MOOD_ACCESS_SETTING && (
        <SettingRow
          label="Allow Player AI to access my mood history"
          description="Player AI can retrieve your mood scores and notes, including past check-ins, to personalize your private conversations. Off by default."
          control={
            <div className="space-y-2 sm:max-w-64 sm:text-right">
              <Switch
                aria-label="Allow Player AI to access my mood history"
                checked={settings.allow_mood_context ?? false}
                disabled={state.pending}
                onChange={(enabled) =>
                  void state.save({ allow_mood_context: enabled })
                }
              />
              <p className="text-caption text-pb-text-muted">
                Turning this off stops further reads, including in conversations
                already open. It cannot withdraw what was already said.
              </p>
            </div>
          }
        />
      )}
      <SettingRow
        label="Auto approval"
        description="Automatically approve eligible actions across all your conversations."
        control={
          <div className="space-y-2 sm:max-w-64 sm:text-right">
            <Switch
              aria-label="Auto approval"
              checked={settings.auto_approve_internal_tools}
              disabled={state.pending}
              onChange={(enabled) =>
                void state.save({ auto_approve_internal_tools: enabled })
              }
            />
            <p className="text-caption text-pb-text-muted">
              Synced with the Player AI pane. Required approvals still apply.
            </p>
          </div>
        }
      />
      <SettingRow
        label="Model"
        description="Default profile for new conversations."
        control={
          <div className="w-full sm:w-72">
            <PersonalProfileControl state={state} />
          </div>
        }
      />
      {/* Kept as today's 13px line: SaveStatus sets 11-12px type, a look
          change no PR 3 row approves. */}
      <p
        role="status"
        aria-live="polite"
        className="mt-5 text-body-small text-pb-text-muted"
      >
        {state.pending
          ? "Saving…"
          : state.error
            ? "Changes not saved"
            : "All changes saved"}
      </p>
      {state.error && (
        <Alert tone="critical" title="Could not save preferences">
          {state.error}
          <Button size="sm" disabled={state.pending} onClick={state.retry}>
            Retry
          </Button>
        </Alert>
      )}
    </SettingsGroup>
  );
}
